import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import RosterTable from './components/RosterTable'
import { useMemo, useState } from 'react'
import AddMemberModal from './components/AddMemberModal'
import RosterSetup from './components/RosterSetup'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { List, LayoutDashboard } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useLocation } from 'react-router-dom'

const TeamRoster = () => {
  const { t } = useTranslation()
  const { team, guild } = useTeam()
  const [addingMembers, setAddingMembers] = useState(false)
  const { hash: initialView } = useLocation()
  const [activeView, setActiveView] = useState(
    initialView ? initialView.replace('#', '') : 'setup',
  )

  const handleViewChange = (view: string) => {
    setActiveView(view)
    history.replaceState({}, '', `#${view}`)
  }

  const excludedTeamRankIds = useMemo(
    () => team.teamRanks?.filter((r) => !r.roster_visibility).map((r) => r.id),
    [team.teamRanks],
  )

  const filteredMembers = useMemo(
    () =>
      team.members?.filter(
        (m) => !excludedTeamRankIds?.includes(m.teamRank.id),
      ) ?? [],
    [team.members],
  )

  return (
    <Section title={`Roster | ${team.name} | ${guild.name}`}>
      <Tabs
        defaultValue='setup'
        value={activeView}
        onValueChange={handleViewChange}
      >
        <h2 className='flex justify-between items-center'>
          <span className='flex items-center gap-3'>
            <span>{t('shared.roster')}</span>
            <span className='bg-muted rounded-lg px-3 text-base'>
              {team.members?.length}
            </span>
          </span>

          <div className='flex items-center gap-3'>
            <TabsList className='h-9'>
              <TabsTrigger value='setup'>
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <LayoutDashboard size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('dashboard.team.roster.setup-view')}
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
              <TabsTrigger value='list'>
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <List size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('dashboard.team.roster.list-view')}
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
            </TabsList>

            <ProtectedContent
              section='roster'
              requirement='admin'
              renderUnauthorized={false}
            >
              <Button size='sm' onClick={() => setAddingMembers(true)}>
                {t('dashboard.team.roster.add-members')}
              </Button>
            </ProtectedContent>
          </div>
        </h2>

        {addingMembers && (
          <AddMemberModal open={addingMembers} setOpen={setAddingMembers} />
        )}

        <TabsContent value={'list'} id={'list'}>
          <RosterTable members={team.members ?? []} />
        </TabsContent>

        <TabsContent value={'setup'} id={'setup'}>
          <RosterSetup members={filteredMembers} />
        </TabsContent>
      </Tabs>
    </Section>
  )
}

export default TeamRoster
